import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  BaseDataObjectGroupDto,
  cleanArray,
  DataObjectGroupType,
  DirectoryTableProps,
  PermissionCode,
  Table,
  TableOperationButton,
  TextEllipsis,
  useHasPermission,
} from '@atlas-frontend/sdk';
import { useMemo } from 'react';
import translations from './DataObjectGroupDirectoryTable.translations.json';
import { createColumnHelper } from '@tanstack/react-table';
import { UiLink } from '../../paths/UiLink';
import { path } from '../../paths';
import { Flex } from 'antd';
import * as Icon from 'react-feather';
import { colorGray } from '@atlas-frontend/design-tokens';
import { useNavigateTo } from '../../paths/useNavigateTo';

const { addTranslations, useTranslation } = i18nNsFactory(
  'data-object-group-directory-table'
);
addTranslations(translations);

const columnHelper = createColumnHelper<BaseDataObjectGroupDto>();

export type DataObjectGroupDirectoryTableProps = DirectoryTableProps<any>;

export function DataObjectGroupDirectoryTable(
  props: DataObjectGroupDirectoryTableProps
) {
  const { t } = useTranslation();
  const navigateToDataObjectGroupCreate = useNavigateTo(
    path.dataObjectGroupCreate
  );
  const hasPermissionToManage = useHasPermission(
    PermissionCode.DATA_OBJECT_GROUP_MANAGE
  );

  const columns = useMemo(
    () =>
      cleanArray([
        columnHelper.accessor('code', {
          header: t('column.code'),
          meta: {
            width: '20ch',
          },
          cell: (props) => (
            <TextEllipsis>
              <UiLink
                id={`data-object-group-${props.row.index}`}
                path={path.dataObjectGroupDetails}
                params={{ id: props.row.original.id }}
              >
                {props.renderValue()}
              </UiLink>
            </TextEllipsis>
          ),
        }),
        columnHelper.accessor('name', {
          header: t('column.name'),
          meta: {
            minWidth: '15ch',
          },
        }),
        columnHelper.accessor(
          (rowData) => t('groupType.' + rowData.groupType),
          {
            id: 'groupType',
            header: t('column.type'),
            filterFn: 'includesOption',
            meta: {
              width: '15ch',
              className: 'group-type',
            },
          }
        ),
        columnHelper.accessor('description', {
          header: t('column.description'),
          meta: {
            minWidth: '30ch',
          },
        }),
        hasPermissionToManage &&
          columnHelper.display({
            id: 'operations',
            header: () => (
              <Flex justify="center" gap="small">
                <TableOperationButton
                  id="create-data-object-group"
                  icon={<Icon.Plus size={22} color={colorGray[7]} />}
                  onClick={() => navigateToDataObjectGroupCreate()}
                  title={t('button.add-data-object-group')}
                />
              </Flex>
            ),
            enableSorting: false,
            enableColumnFilter: false,
            meta: {
              width: 50,
              className: 'sticky-right',
            },
          }),
      ]),
    [navigateToDataObjectGroupCreate, t, hasPermissionToManage]
  );

  const options = useMemo(
    () => ({
      groupType: Object.values(DataObjectGroupType).map((type) => ({
        id: type,
        name: t('groupType.' + type),
      })),
    }),
    [t]
  );

  return (
    <Table
      name="data-object-group-directory-table"
      columns={columns}
      options={options}
      hideClearFilters
      manualFiltering
      manualSorting
      scrollContainerSelector="#data-object-group-content"
      sticky
      virtual
      {...props}
    />
  );
}
